import { useCallback, useEffect, useState } from 'react';
import { ApiError, type ApiClient } from '../api';
import { BRAND_META, BRAND_ORDER } from '../brand';
import { compact, timeAgo } from '../format';
import type {
  CompetitorCell,
  CompetitorPlatform,
  CompetitorRow,
  CompetitorSet,
  CompetitorsResponse,
  Me,
} from '../types';

/**
 * The competitor tracking panel (decision of 30 August 2026): per brand
 * set, competitors and our own accounts ranked by follower count across
 * Instagram, TikTok and X, the trend since the last reading, and — for
 * the CashBlack and Afrofiliate sets — the distance to the 10,000
 * total-follower goal. The EqualiShop set deliberately carries no goal
 * framing.
 *
 * The numbers come from a once-a-day polite scrape of public profile
 * pages, with manual entry as the storage layer underneath: a cell whose
 * scrape failed says so and offers the entry field rather than passing an
 * old number off as current, and a handle the resolver could not verify
 * asks for one. Owner and operator can type; analyst reads.
 *
 * Identity is never colour alone (dataviz rules): our own row carries the
 * brand swatch AND the "us" chip, and every state has words, not just a
 * glyph.
 */

const PLATFORM_LABELS: Record<CompetitorPlatform, string> = {
  instagram: 'Instagram',
  tiktok: 'TikTok',
  x: 'X',
};
const PLATFORMS: CompetitorPlatform[] = ['instagram', 'tiktok', 'x'];

/**
 * Platform marks, inline SVG in currentColor: the CSP allows no
 * third-party images, and a mark never replaces its text label
 * (identity is never a glyph alone, same rule as colour).
 */
function PlatformGlyph({ platform }: { platform: CompetitorPlatform }) {
  const common = { width: 12, height: 12, viewBox: '0 0 24 24', 'aria-hidden': true as const };
  switch (platform) {
    case 'instagram':
      return (
        <svg {...common} fill="none" stroke="currentColor" strokeWidth="2">
          <rect x="2.8" y="2.8" width="18.4" height="18.4" rx="5.2" />
          <circle cx="12" cy="12" r="4.4" />
          <circle cx="17.4" cy="6.6" r="1.3" fill="currentColor" stroke="none" />
        </svg>
      );
    case 'tiktok':
      return (
        <svg {...common} fill="currentColor">
          <path d="M16.6 5.82A4.28 4.28 0 0 1 12.98 2h-3.1v12.68a2.59 2.59 0 1 1-1.84-2.48V9.02a5.74 5.74 0 1 0 5.02 5.66V7.9a7.35 7.35 0 0 0 4.28 1.36V6.14a4.3 4.3 0 0 1-.74-.32Z" />
        </svg>
      );
    case 'x':
      return (
        <svg {...common} fill="currentColor">
          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231ZM17.083 19.77h1.833L7.084 4.126H5.117Z" />
        </svg>
      );
  }
}

/** Loaded avatars, one fetch per account per page life; object URLs are
 *  tiny and the map caps at the tracked-account count. */
const avatarUrls = new Map<string, Promise<string>>();

function loadAvatar(api: ApiClient, accountId: string): Promise<string> {
  let p = avatarUrls.get(accountId);
  if (!p) {
    p = api.getBlob(`/api/competitors/avatar/${accountId}`).then((b) => URL.createObjectURL(b));
    p.catch(() => avatarUrls.delete(accountId)); // a failed load may retry next mount
    avatarUrls.set(accountId, p);
  }
  return p;
}

/** Bytes a logo upload will accept; the server re-reads the type from the
 *  file itself, so this only spares an obvious round trip. */
const LOGO_MAX_BYTES = 262_144;

/**
 * The row's face: the brand's own logo where someone has supplied one,
 * else the account's profile thumbnail where the crawl found one, else a
 * monogram - brand hue ground for our own rows (the "us" chip still says
 * it in words), neutral for competitors.
 *
 * Owners and operators can click the face and pick a file. Instagram
 * serves no logged-out thumbnail at all, so for a good many rows a human
 * supplying the mark is the only way one ever appears; it lands on all
 * three platform columns at once and the daily crawl leaves it alone
 * afterwards.
 */
function Avatar({
  api,
  accountId,
  logoAccountId,
  name,
  own,
  series,
  canWrite,
  onUploaded,
  onNotice,
}: {
  api: ApiClient;
  accountId: string | null;
  logoAccountId: string | null;
  name: string;
  own: boolean;
  series: string;
  canWrite: boolean;
  onUploaded: () => void;
  onNotice: (n: string | null) => void;
}) {
  const [url, setUrl] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  // Bumped after an upload. The row's avatar account id often does not
  // change when a logo replaces an existing one, so without this the
  // effect would not re-run and the face would stay the old image until
  // the next mount.
  const [version, setVersion] = useState(0);
  useEffect(() => {
    setUrl(null);
    if (!accountId) return;
    let alive = true;
    loadAvatar(api, accountId).then(
      (u) => alive && setUrl(u),
      () => undefined, // fall through to the monogram
    );
    return () => {
      alive = false;
    };
  }, [api, accountId, version]);

  const face =
    accountId && url ? (
      <img className="row-avatar" src={url} alt="" loading="lazy" />
    ) : (
      <span
        className="row-monogram"
        style={own ? { background: series, color: '#0d0d0d', borderColor: series } : undefined}
        aria-hidden
      >
        {name.trim().charAt(0).toUpperCase()}
      </span>
    );

  if (!canWrite || !logoAccountId) return face;

  const upload = async (file: File) => {
    if (file.size > LOGO_MAX_BYTES) {
      onNotice(`${name}: that file is ${Math.round(file.size / 1024)} KB; the limit is 256 KB.`);
      return;
    }
    setUploading(true);
    onNotice(null);
    try {
      await api.postBinary(`/api/competitors/accounts/${logoAccountId}/logo`, file);
      // The stored bytes changed under a URL the cache keys on, so the
      // cached object URL for this row has to go before the reload.
      if (accountId) avatarUrls.delete(accountId);
      avatarUrls.delete(logoAccountId);
      setVersion((v) => v + 1);
      onUploaded();
    } catch (err) {
      const code = err instanceof ApiError ? (err.code ?? String(err.status)) : String(err);
      onNotice(
        code === 'unsupported_type'
          ? `${name}: that file is not a PNG, JPEG or WebP.`
          : `${name}: logo upload failed (${code}).`,
      );
    } finally {
      setUploading(false);
    }
  };

  return (
    <label
      className={`row-face${uploading ? ' uploading' : ''}`}
      title={`Upload a logo for ${name}. PNG, JPEG or WebP, up to 256 KB.`}
    >
      {face}
      <span className="row-face-hint" aria-hidden>
        {uploading ? '…' : '+'}
      </span>
      <input
        type="file"
        aria-label={`Upload a logo for ${name}`}
        accept="image/png,image/jpeg,image/webp"
        disabled={uploading}
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = ''; // same file twice must still fire
          if (file) void upload(file);
        }}
      />
    </label>
  );
}

type Editing =
  | { accountId: string; kind: 'count'; value: string }
  | { accountId: string; kind: 'handle'; value: string };

export function CompetitorsView({ api, me }: { api: ApiClient; me: Me }) {
  const [data, setData] = useState<CompetitorsResponse | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [editing, setEditing] = useState<Editing | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setData(await api.get<CompetitorsResponse>('/api/competitors'));
      setError(null);
    } catch (err) {
      setError(err as Error);
    }
  }, [api]);

  useEffect(() => {
    void load();
    const id = window.setInterval(() => void load(), 300_000); // daily data; 5 min is plenty
    return () => window.clearInterval(id);
  }, [load]);

  const canWrite = (me.role === 'owner' || me.role === 'operator') && data?.manualEntry === true;

  const save = async () => {
    if (!editing) return;
    setBusy(true);
    setNotice(null);
    try {
      if (editing.kind === 'count') {
        const n = Number(editing.value.replaceAll(',', '').trim());
        if (!Number.isInteger(n) || n < 0) {
          setNotice('A follower count is a whole number, 0 or more.');
          return;
        }
        await api.post(`/api/competitors/accounts/${editing.accountId}/reading`, {
          follower_count: n,
        });
      } else {
        const handle = editing.value.trim();
        if (!/^@?[A-Za-z0-9._]{1,64}$/.test(handle)) {
          setNotice('Handles are letters, digits, dots and underscores.');
          return;
        }
        await api.post(`/api/competitors/accounts/${editing.accountId}/handle`, { handle });
      }
      setEditing(null);
      await load();
    } catch (err) {
      setNotice(`That did not save: ${(err as Error).message}`);
    } finally {
      setBusy(false);
    }
  };

  if (error && !data) {
    return <div className="load-error">The read model is unreachable: {error.message}</div>;
  }
  if (!data) return <div className="empty">Loading competitor tracking…</div>;

  // Every cell waiting on a human, across all sets, as one worklist:
  // clicking around a table to fill 30 gaps is how gaps stay unfilled.
  const gaps = data.sets.flatMap((set) =>
    set.rows.flatMap((row) =>
      PLATFORMS.flatMap((platform) => {
        const cell = row.platforms[platform];
        if (!cell || cell.accountId === null) return [];
        if (cell.state !== 'needs_handle' && cell.state !== 'needs_manual') return [];
        return [{ setLabel: set.label, name: row.name, platform, cell }];
      }),
    ),
  );

  const sets = [...data.sets].sort(
    (a, b) => BRAND_ORDER.indexOf(a.brand) - BRAND_ORDER.indexOf(b.brand) || a.key.localeCompare(b.key),
  );

  return (
    <div className="grid" style={{ marginTop: 14 }}>
      {notice && (
        <div className="queue-notice" role="alert">
          {notice}
        </div>
      )}
      {canWrite && gaps.length > 0 && (
        <GapFiller api={api} gaps={gaps} onSaved={() => void load()} onNotice={setNotice} />
      )}
      {sets.map((set) => (
        <SetCard
          key={set.key}
          api={api}
          set={set}
          canWrite={canWrite}
          busy={busy}
          editing={editing}
          onEdit={setEditing}
          onSave={() => void save()}
          onReload={() => void load()}
          onNotice={setNotice}
        />
      ))}
      <p className="competitors-provenance">
        Counts are read once a day from each account's public profile page. Instagram serves that
        page to search engines and a login wall to us, so Instagram counts come from the search
        index instead and are marked <em>via search</em> — a public number, but as fresh as the
        last crawl of it rather than as of today. X does not serve counts to logged-out readers at
        all, so X columns are kept by hand. A number a failed scrape has left behind is flagged
        stale, never passed off as current. Logos: click any face to upload one.
      </p>
    </div>
  );
}

interface Gap {
  setLabel: string;
  name: string;
  platform: CompetitorPlatform;
  cell: CompetitorCell;
}

/**
 * The worklist for everything the scraper honestly could not get: missing
 * handles, and counts a platform will not serve logged out (Instagram
 * serves nothing; X only counts for handles a human vouched for). One
 * sitting instead of thirty clicks.
 */
function GapFiller({
  api,
  gaps,
  onSaved,
  onNotice,
}: {
  api: ApiClient;
  gaps: Gap[];
  onSaved: () => void;
  onNotice: (n: string | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const [drafts, setDrafts] = useState<Record<string, { handle: string; count: string }>>({});
  const [savingId, setSavingId] = useState<string | null>(null);

  const draftFor = (id: string) => drafts[id] ?? { handle: '', count: '' };
  const setDraft = (id: string, patch: Partial<{ handle: string; count: string }>) =>
    setDrafts((d) => ({ ...d, [id]: { ...draftFor(id), ...patch } }));

  const save = async (gap: Gap) => {
    const id = gap.cell.accountId!;
    const draft = draftFor(id);
    const handle = draft.handle.trim();
    const count = draft.count.replaceAll(',', '').trim();
    if (!handle && !count) return;
    setSavingId(id);
    onNotice(null);
    try {
      if (handle) await api.post(`/api/competitors/accounts/${id}/handle`, { handle });
      if (count) {
        const n = Number(count);
        if (!Number.isInteger(n) || n < 0) {
          onNotice(`${gap.name}: a follower count is a whole number, 0 or more.`);
          return;
        }
        await api.post(`/api/competitors/accounts/${id}/reading`, { follower_count: n });
      }
      setDrafts((d) => {
        const next = { ...d };
        delete next[id];
        return next;
      });
      onSaved();
    } catch (err) {
      onNotice(`${gap.name} did not save: ${(err as Error).message}`);
    } finally {
      setSavingId(null);
    }
  };

  return (
    <div className="card gap-filler">
      <button className="gap-head" onClick={() => setOpen(!open)} aria-expanded={open}>
        <span className="card-title">Fill in the gaps</span>
        <span className="gap-count">
          {gaps.length} {gaps.length === 1 ? 'cell needs' : 'cells need'} a hand · {open ? 'hide' : 'show'}
        </span>
      </button>
      {open && (
        <div className="gap-list">
          {gaps.map((gap) => {
            const id = gap.cell.accountId!;
            const draft = draftFor(id);
            const needsHandle = gap.cell.state === 'needs_handle';
            return (
              <div key={id} className="gap-row">
                <span className="gap-who">
                  <PlatformGlyph platform={gap.platform} />
                  <span>
                    {gap.name} <span className="gap-set">{gap.setLabel}</span>
                  </span>
                </span>
                {needsHandle && (
                  <input
                    className="cell-input gap-input"
                    placeholder="@handle"
                    value={draft.handle}
                    onChange={(e) => setDraft(id, { handle: e.target.value })}
                    onKeyDown={(e) => e.key === 'Enter' && void save(gap)}
                  />
                )}
                <input
                  className="cell-input gap-input"
                  inputMode="numeric"
                  placeholder="followers"
                  value={draft.count}
                  onChange={(e) => setDraft(id, { count: e.target.value })}
                  onKeyDown={(e) => e.key === 'Enter' && void save(gap)}
                />
                <button
                  className="cell-save"
                  disabled={savingId === id || (!draft.handle.trim() && !draft.count.trim())}
                  onClick={() => void save(gap)}
                >
                  save
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function SetCard({
  api,
  set,
  canWrite,
  busy,
  editing,
  onEdit,
  onSave,
  onReload,
  onNotice,
}: {
  api: ApiClient;
  set: CompetitorSet;
  canWrite: boolean;
  busy: boolean;
  editing: Editing | null;
  onEdit: (e: Editing | null) => void;
  onSave: () => void;
  onReload: () => void;
  onNotice: (n: string | null) => void;
}) {
  const meta = BRAND_META[set.brand];
  const ranked = set.rows.filter((r) => r.rank !== null).length;
  const newest = set.rows
    .flatMap((r) => PLATFORMS.map((p) => r.platforms[p]?.readAt ?? null))
    .filter((t): t is string => t !== null)
    .sort()
    .at(-1);

  return (
    <div className="card">
      <div className="card-head">
        <span className="card-title">
          <span className="swatch" style={{ background: meta.series }} aria-hidden /> {set.label}
        </span>
        <span className="card-note">{newest ? `latest reading ${timeAgo(newest)}` : 'no readings yet'}</span>
      </div>

      <OwnSummary set={set} ranked={ranked} />

      <div className="table-wrap">
        <table className="data competitors">
          <thead>
            <tr>
              <th className="num">#</th>
              <th>Account</th>
              {PLATFORMS.map((p) => (
                <th key={p} className="num">
                  <span className="platform-head">
                    <PlatformGlyph platform={p} />
                    {PLATFORM_LABELS[p]}
                  </span>
                </th>
              ))}
              <th className="num">Total</th>
              <th className="num">Since last reading</th>
            </tr>
          </thead>
          <tbody>
            {set.rows.map((row) => (
              <tr key={row.name} className={row.own ? 'own-row' : undefined}>
                <td className="num rank">{row.rank ?? '–'}</td>
                <td className="account-cell">
                  <Avatar
                    api={api}
                    accountId={row.avatarAccountId}
                    logoAccountId={row.logoAccountId}
                    name={row.name}
                    own={row.own}
                    series={meta.series}
                    canWrite={canWrite}
                    onUploaded={onReload}
                    onNotice={onNotice}
                  />
                  <span className="account-name">{row.name}</span>
                  {/* The own-row chip used to read "us", meaning "this is
                      us" - which sat after "CashBlack UK" and read as the
                      COUNTRY, "Cashblack US" (Matthew, 7 September 2026).
                      Market sets now wear their market; others say ours.
                      A text chip, not a flag emoji: Windows renders
                      country flags as bare letter pairs, so an emoji flag
                      here would just recreate the same misreading. */}
                  {row.own && (
                    <span className={`own-chip${set.key.endsWith('-uk') ? ' market-uk' : set.key.endsWith('-us') ? ' market-us' : ''}`}>
                      {set.key.endsWith('-uk') ? 'UK · ours' : set.key.endsWith('-us') ? 'US · ours' : 'ours'}
                    </span>
                  )}
                </td>
                {PLATFORMS.map((p) => (
                  <Cell
                    key={p}
                    cell={row.platforms[p]}
                    canWrite={canWrite}
                    busy={busy}
                    editing={editing}
                    onEdit={onEdit}
                    onSave={onSave}
                  />
                ))}
                <td className="num total">{row.total === null ? '–' : compact(row.total)}</td>
                <td className="num">
                  <Trend trend={row.trend} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** Own position at a glance, above the table (and the goal, where a set has one). */
function OwnSummary({ set, ranked }: { set: CompetitorSet; ranked: number }) {
  const { own } = set;
  if (own.total === null) {
    return (
      <p className="own-summary">
        No readings for {own.name}'s own accounts yet
        {set.goalTotalFollowers !== null
          ? ` — the ${compact(set.goalTotalFollowers)} total-follower goal starts counting with the first one.`
          : '.'}
      </p>
    );
  }
  const goal = set.goalTotalFollowers;
  return (
    <div className="own-summary">
      <p>
        <strong>{own.name}</strong> sits <strong>#{own.rank}</strong> of {ranked} ·{' '}
        {compact(own.total)} followers across platforms
        {own.trend !== null && <> · <Trend trend={own.trend} /> since the last reading</>}
        {goal !== null &&
          (own.total >= goal ? (
            <> · past the {compact(goal)} goal</>
          ) : (
            <>
              · <strong>{compact(goal - own.total)}</strong> to go to {compact(goal)}
            </>
          ))}
      </p>
      {goal !== null && (
        <div
          className="goal-bar"
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={goal}
          aria-valuenow={Math.min(own.total, goal)}
          aria-label={`${own.name} progress to ${goal} followers`}
        >
          <div
            className="goal-fill"
            style={{
              width: `${Math.min(100, (own.total / goal) * 100)}%`,
              background: BRAND_META[set.brand].series,
            }}
          />
        </div>
      )}
    </div>
  );
}

function Trend({ trend }: { trend: number | null }) {
  if (trend === null) return <span className="trend-none">–</span>;
  if (trend === 0) return <span className="trend-none">±0</span>;
  // Direction is glyph + sign, never colour (dataviz: text wears text tokens).
  return (
    <span className="trend">
      {trend > 0 ? '▲' : '▼'} {trend > 0 ? '+' : '−'}
      {compact(Math.abs(trend))}
    </span>
  );
}

function Cell({
  cell,
  canWrite,
  busy,
  editing,
  onEdit,
  onSave,
}: {
  cell: CompetitorCell | undefined;
  canWrite: boolean;
  busy: boolean;
  editing: Editing | null;
  onEdit: (e: Editing | null) => void;
  onSave: () => void;
}) {
  if (!cell) return <td className="num untracked">·</td>; // platform not tracked

  const isEditing = editing !== null && cell.accountId !== null && editing.accountId === cell.accountId;
  if (isEditing) {
    return (
      <td className="num cell-editing">
        <input
          autoFocus
          className="cell-input"
          inputMode={editing.kind === 'count' ? 'numeric' : 'text'}
          placeholder={editing.kind === 'count' ? 'count' : '@handle'}
          value={editing.value}
          disabled={busy}
          onChange={(e) => onEdit({ ...editing, value: e.target.value })}
          onKeyDown={(e) => {
            if (e.key === 'Enter') onSave();
            if (e.key === 'Escape') onEdit(null);
          }}
        />
        <button className="cell-save" disabled={busy} onClick={onSave}>
          save
        </button>
      </td>
    );
  }

  const editCount = () =>
    cell.accountId && onEdit({ accountId: cell.accountId, kind: 'count', value: '' });
  const editHandle = () =>
    cell.accountId && onEdit({ accountId: cell.accountId, kind: 'handle', value: cell.handle ?? '' });

  switch (cell.state) {
    case 'needs_handle':
      return (
        <td className="num">
          <span className="cell-flag">no handle found</span>
          {canWrite && (
            <button className="cell-action" onClick={editHandle}>
              add handle
            </button>
          )}
        </td>
      );
    case 'needs_manual':
      return (
        <td className="num">
          <span className="cell-flag">
            {cell.scrapeStatus === 'unsupported' ? 'manual only' : 'scrape failed'}
          </span>
          {canWrite && (
            <button className="cell-action" onClick={editCount}>
              enter count
            </button>
          )}
        </td>
      );
    case 'pending':
      return (
        <td className="num">
          <span
            className="cell-pending"
            title={
              cell.accountId === null
                ? 'In the config; the scraper seeds it on its next daily run'
                : 'Awaiting the first reading'
            }
          >
            …
          </span>
          {canWrite && cell.accountId !== null && (
            <button className="cell-action" onClick={editCount}>
              enter count
            </button>
          )}
        </td>
      );
    case 'stale':
      return (
        <td className="num">
          <span className="followers stale" title={`Last good reading ${cell.readAt ? timeAgo(cell.readAt) : ''}`}>
            {compact(cell.followers!)}
          </span>
          <span className="cell-flag">stale · {cell.readAt ? timeAgo(cell.readAt) : ''}</span>
          {canWrite && (
            <button className="cell-action" onClick={editCount}>
              update
            </button>
          )}
        </td>
      );
    case 'ok':
      return (
        <td className="num">
          <span
            className="followers"
            title={`@${cell.handle} · read ${cell.readAt ? timeAgo(cell.readAt) : ''} (${cell.source})`}
          >
            {compact(cell.followers!)}
          </span>
          {cell.source === 'graph' && (
            <span
              className="cell-note"
              title="Read from Instagram itself, through a token this account granted us. Exact and current, unlike a crawl or a search snippet."
            >
              via Instagram
            </span>
          )}
          {cell.source === 'search' && (
            <span
              className="cell-note"
              title="Instagram serves this crawler a login wall, so the count comes from the search index: public, but as fresh as the last crawl of it."
            >
              via search
            </span>
          )}
          {canWrite && (
            <button className="cell-action quiet" onClick={editCount} aria-label="Enter a corrected count">
              edit
            </button>
          )}
        </td>
      );
  }
}
